import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTabNav } from "../hooks/useTabNav";
import axios from "axios";
import { usePageNavigate } from "../hooks/usePageNavigate";
import {
  Loader2, DollarSign, TrendingUp, Briefcase, BarChart3, ChevronDown, ChevronUp,
  X, CheckCircle, Clock, AlertCircle, FileText, MapPin, Download, Send,
  PieChart, ArrowUpRight, Percent, Calendar, Wallet, Banknote, Plus, Shield,
  Building2, AlertTriangle, Zap, TrendingUp as TrendUp2, BookOpen, Settings,
  CheckCircle2, User, MessageCircle, Eye, ChevronRight
} from "lucide-react";
import { Card, GoldButton } from "../components/shared/UIComponents";
import { PortfolioCharts } from "../components/shared/PortfolioCharts";
import { downloadReport } from "../utils/downloadReport";
import { toast } from "sonner";
import { fmtUSD } from "../utils/currency";
import { SocialProofBadge, LivePulse } from "../components/shared/QuickActions";
import AutoInvestPreferences from "../components/AutoInvestPreferences";
import InvestorFundsTab from "../components/investor/InvestorFundsTab";
import DealHistoryTab from "../components/investor/DealHistoryTab";
import InvestmentProcessTab from "../components/investor/InvestmentProcessTab";
import OpportunityDetailView from "../components/investor/OpportunityDetailView";
import ExpressCommitModal from "../components/investor/ExpressCommitModal";
import DealQA from "../components/investor/DealQA";
import EntityComms from "../components/shared/EntityComms";
import TaxExportPanel from "../components/shared/TaxExportPanel";
import EntitlementBanner from "../components/shared/EntitlementBanner";
import { InvestorServicingPanel } from "../components/shared/ServicingPanel";
import ProfileEditor from "../components/shared/ProfileEditor";
import { OpportunityScoreCard } from "../components/shared/ScoreCards";
import { useMyRequestCounts } from "../hooks/useCommCounts";
import usePullToRefresh from "../hooks/usePullToRefresh";
import InvestorPortfolioPanel from "../components/investor/InvestorPortfolioPanel";
import { formatAddress } from "../utils/addressFormat";
import {
  WaitlistBanner, FundOpportunityCard, FundMemoModal,
  SubscriptionModal, FundPortfolioPanel, FundUpdatesPanel
} from "../components/investor/FundPortalComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };
const STAGE_LABELS = { capital_matching: "Capital Matching", terms: "Terms", funding: "Funding", funded: "Funded", approved: "Approved", underwriting: "Underwriting", in_review: "In Review", submitted: "Submitted", needs_info: "Needs Info", active: "Active", declined: "Declined", new_lead: "New Lead" };

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-blue-100 text-blue-700",
  funded: "bg-emerald-100 text-emerald-700",
  withdrawn: "bg-gray-100 text-gray-500",
  open: "bg-emerald-100 text-emerald-700",
  closed: "bg-gray-100 text-gray-600",
  paused: "bg-amber-100 text-amber-700",
};

const TYPE_COLORS = [
  "bg-emerald-500", "bg-blue-500", "bg-amber-500", "bg-purple-500",
  "bg-rose-500", "bg-cyan-500", "bg-orange-500", "bg-indigo-500",
];

export default function InvestorDashboard() {
  const { navigateTo, navigateToEntity } = usePageNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [commitModal, setCommitModal] = useState(null);
  const [expressModal, setExpressModal] = useState(null);
  const [accredStatus, setAccredStatus] = useState(null);
  const [quickFundMap, setQuickFundMap] = useState({});
  const { openCount: commOpenCount } = useMyRequestCounts();
  const [allNotifs, setAllNotifs] = useState([]);
  const [feedCollapsed, setFeedCollapsed] = useState(false);
  const [dashSection, setDashSection] = useTabNav("section", "fund_portal");

  const fetchNotifications = () => {
    axios.get(`${API}/notifications`, { headers }).then(r => {
      setAllNotifs(r.data?.notifications || r.data || []);
    }).catch(() => {});
  };

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Filter notifications — investors only see fund-related activity, not individual loan deals
  const investorNotifs = useMemo(() => {
    const fundTypes = new Set(["new_opportunity", "commitment_confirmed", "commitment_pending", "distribution_posted", "deal_funded", "fund_update"]);
    const loanKeywords = ["fix & flip", "fix flip", "bridge", "dscr", "new construction", "hard money"];
    return allNotifs.filter(n => {
      // Always show commitment/distribution/fund notifications
      if (["commitment_confirmed", "commitment_pending", "distribution_posted", "deal_funded", "fund_update"].includes(n.type)) return true;
      // For new_opportunity — only if it references a fund, not a loan deal
      const text = ((n.title || "") + " " + (n.message || "")).toLowerCase();
      if (loanKeywords.some(kw => text.includes(kw))) return false;
      return true;
    });
  }, [allNotifs]);
  const [funding, setFunding] = useState(false);
  const [fundingAmount, setFundingAmount] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/investor/dashboard`, { headers });
      setData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const { pullIndicator } = usePullToRefresh(fetchData);

  useEffect(() => {
    fetchData();
    fetchNotifications();
    axios.get(`${API}/investor/profile`, { headers }).then(r => setAccredStatus(r.data.accreditation_status)).catch(() => {});
    axios.get(`${API}/investor/quick-fund-indicators`, { headers }).then(r => {
      const map = {};
      (r.data.indicators || []).forEach(ind => { map[ind.opportunity_id] = ind; });
      setQuickFundMap(map);
    }).catch(() => {});
    const nInterval = setInterval(fetchNotifications, 20000);
    return () => clearInterval(nInterval);
  }, [fetchData]);

  const handleFundAccount = async () => {
    const amt = parseFloat(fundingAmount);
    if (!amt || amt <= 0) return;
    setFunding(true);
    try {
      await axios.post(`${API}/capital/account/fund`, { amount: amt, notes: "Manual deposit" }, { headers });
      setFundingAmount("");
      fetchData();
    } catch (err) { console.error(err); }
    finally { setFunding(false); }
  };

  // Derive all data (safe even when data is null during loading)
  const portfolio = data?.portfolio || {};
  const opportunities = data?.open_opportunities || [];
  const myCommitments = data?.my_commitments || [];
  const wallet = data?.wallet || {};
  const recentDists = data?.recent_distributions || [];
  const entitlement = data?.entitlement_state || "unverified";
  const ytd = data?.ytd_summary || {};

  const computedState = user?.computed_state || {};
  const canCommitCapital = computedState.can_commit_capital || false;
  const approvalState = computedState.approval_state || user?.approval_status || "conditional_access";
  const accredStatus_computed = computedState.accreditation_status || user?.accreditation_status || "not_submitted";
  const isApproved = approvalState === "approved";
  const isFullyEligible = canCommitCapital;

  // Build interleaved feed (hook MUST be called unconditionally)
  const feedItems = useMemo(() => {
    const items = [];
    opportunities.forEach(opp => {
      items.push({ type: "opportunity", opp, ts: opp.created_at || "" });
    });
    allNotifs.slice(0, 15).forEach(n => {
      items.push({ type: "activity", notif: n, ts: n.created_at || "" });
    });
    items.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    return items;
  }, [opportunities, allNotifs]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
  );

  const activeCommits = myCommitments.filter(c => c.status !== "withdrawn");

  const NAV_ITEMS = [
    { id: "fund_portal", label: "Fund Portal", icon: Building2 },
    { id: "opportunities", label: `Opportunities (${opportunities.length})`, icon: Briefcase },
    { id: "commitments", label: `Commitments (${activeCommits.length})`, icon: DollarSign },
    { id: "distributions", label: "Distributions", icon: Banknote },
    { id: "portfolio", label: "Portfolio", icon: PieChart },
    { id: "funds", label: "My Funds", icon: Wallet },
    { id: "servicing", label: "Servicing", icon: DollarSign },
    { id: "auto_invest", label: "Auto-Invest", icon: Settings },
    { id: "how_it_works", label: "How It Works", icon: BookOpen },
    { id: "messages", label: "Messages", icon: MessageCircle },
    { id: "profile", label: "My Profile", icon: Shield },
  ];

  return (
    <div className="min-h-screen" style={{ background: "var(--ic-bg-primary)" }} data-testid="investor-dashboard">
      {pullIndicator}

      {/* ── TICKER BAR ─────────────────────────── */}
      <div style={{ background: "var(--ic-bg-row-alt)", borderBottom: "1px solid var(--ic-border)" }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6 py-2.5 overflow-x-auto scrollbar-hide">
            <LivePulse label="Portfolio Live" />
            <div style={{ height: 12, width: 1, background: "var(--ic-border-dark)" }} />
            {[
              { label: "PORTFOLIO", value: fmtUSD(wallet.total_committed || 0) },
              { label: "RETURNS", value: fmtUSD(wallet.total_distributions || 0), color: "var(--ic-green-dark)" },
              { label: "YIELD", value: `${portfolio?.weighted_avg_yield || 0}%`, color: "var(--ic-gold)" },
              { label: "DEALS", value: `${myCommitments?.length || 0}` },
              { label: "AVAILABLE", value: fmtUSD(wallet.available_capital || 0), color: "var(--ic-green-mid)" },
              { label: "OPPS", value: opportunities.length },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-2 whitespace-nowrap">
                <span style={{ fontSize: 9, fontWeight: 500, color: "var(--ic-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t.label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "monospace", color: t.color || "var(--ic-text-primary)" }}>{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3-COLUMN FEED LAYOUT ─────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] xl:grid-cols-[260px_1fr_280px] gap-6">

          {/* ── LEFT SIDEBAR: Profile + Nav ── */}
          <div className="hidden lg:block">
            <div className="sticky top-6 space-y-4">
              {/* Profile Card */}
              <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }}>
                <div style={{ height: 56, background: "var(--ic-green-dark)", position: "relative" }} />
                <div style={{ padding: "0 16px 16px", marginTop: -24, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--ic-green-dark)", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ic-text-inverse)", fontWeight: 700, fontSize: 18, boxShadow: "var(--ic-shadow-md)" }}>
                    {(user?.full_name || "I")[0]}
                  </div>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--ic-text-primary)", marginTop: 8 }}>{user?.full_name}</h3>
                  <p style={{ fontSize: 10, marginTop: 2 }}>
                    {isFullyEligible ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--ic-green-dark)", fontWeight: 600 }}>
                        <Shield className="h-3 w-3" /> Accredited Investor
                      </span>
                    ) : isApproved ? (
                      <span style={{ color: "var(--ic-status-pending)", fontWeight: 600 }}>Accreditation Pending</span>
                    ) : (
                      <span style={{ color: "var(--ic-status-pending)", fontWeight: 600 }}>Conditional Access</span>
                    )}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--ic-border)" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ic-text-primary)" }}>{activeCommits.length}</div>
                      <div style={{ fontSize: 9, color: "var(--ic-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Deals</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ic-green-dark)" }}>{fmtUSD(wallet.total_committed || 0).replace(/\.\d+$/, "")}</div>
                      <div style={{ fontSize: 9, color: "var(--ic-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Deployed</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ic-gold)" }}>{portfolio?.weighted_avg_yield || 0}%</div>
                      <div style={{ fontSize: 9, color: "var(--ic-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Yield</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Nav */}
              <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }}>
                <div style={{ padding: 4 }}>
                  {NAV_ITEMS.map(s => (
                    <button key={s.id} onClick={() => setDashSection(s.id)}
                      data-testid={`investor-nav-${s.id}`}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                        borderRadius: "var(--ic-radius)", fontSize: 12, fontWeight: 600, transition: "all 0.15s ease",
                        border: "none", cursor: "pointer", textAlign: "left",
                        background: dashSection === s.id ? "var(--ic-green-dark)" : "transparent",
                        color: dashSection === s.id ? "var(--ic-text-inverse)" : "var(--ic-text-secondary)",
                      }}
                      onMouseOver={(e) => { if (dashSection !== s.id) e.currentTarget.style.background = "var(--ic-bg-row-alt)"; }}
                      onMouseOut={(e) => { if (dashSection !== s.id) e.currentTarget.style.background = "transparent"; }}
                    >
                      <s.icon style={{ width: 14, height: 14 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{s.label}</span>
                      {s.id === "messages" && commOpenCount > 0 && (
                        <span style={{ padding: "2px 6px", borderRadius: 10, fontSize: 9, fontWeight: 700, background: "rgba(220,38,38,0.1)", color: "var(--ic-status-rejected)" }}>{commOpenCount}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Capital Wallet */}
              <div style={{ borderRadius: "var(--ic-radius-lg)", background: "var(--ic-green-dark)", border: "1px solid var(--ic-green-mid)", padding: 16, boxShadow: "var(--ic-shadow-md)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <Wallet style={{ width: 16, height: 16, color: "var(--ic-gold)" }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Capital Wallet</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "var(--ic-gold)", marginBottom: 12 }}>{fmtUSD(wallet.available_capital || 0)}</div>
                <p style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 12, lineHeight: 1.5 }}>Available balance for investment commitments. Add funds once your accreditation is verified.</p>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="number" value={fundingAmount} onChange={e => setFundingAmount(e.target.value)}
                    placeholder="Amount ($)" data-testid="fund-amount-input"
                    style={{ flex: 1, padding: "8px 12px", borderRadius: "var(--ic-radius)", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", color: "white", fontSize: 13, outline: "none" }} />
                  <button onClick={handleFundAccount} disabled={funding || !fundingAmount}
                    data-testid="fund-account-btn"
                    style={{ padding: "8px 12px", borderRadius: "var(--ic-radius)", background: "var(--ic-green-light)", color: "white", fontSize: 12, fontWeight: 600, border: "none", cursor: "pointer", opacity: (funding || !fundingAmount) ? 0.4 : 1, display: "flex", alignItems: "center", gap: 6 }}>
                    {funding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  </button>
                </div>
                {accredStatus && (
                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 10, fontWeight: 600, color: accredStatus === "verified" ? "var(--ic-gold)" : accredStatus === "pending_review" ? "rgba(217,119,6,0.8)" : "rgba(255,255,255,0.5)" }}>
                    <Shield className="h-3 w-3" />
                    {accredStatus === "verified" ? "Accredited" : accredStatus === "pending_review" ? "Accreditation Pending \u2014 wallet activates upon verification" : "Not Verified"}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── CENTER: The Feed ── */}
          <div className="min-w-0">
            {/* Mobile-only header */}
            <div className="lg:hidden mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-bold text-[#0A0A0A] tracking-tight">
                    Capital Partner, {user?.full_name?.split(" ")[0] || "Investor"}
                  </h1>
                  <p className="text-xs text-[#888] mt-0.5">{opportunities.length} opportunit{opportunities.length !== 1 ? "ies" : "y"} available</p>
                </div>
                {isFullyEligible ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                    <Shield className="h-3 w-3" /> Accredited
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200">
                    Conditional
                  </span>
                )}
              </div>
              {/* Mobile section toggle */}
              <div className="flex gap-1 mt-3 overflow-x-auto scrollbar-hide" data-testid="investor-section-toggle">
                {NAV_ITEMS.slice(0, 5).map(s => (
                  <button key={s.id} onClick={() => setDashSection(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      dashSection === s.id ? "bg-emerald-700 text-white" : "text-[#888] hover:bg-[#FDFCF9] border border-[#EDE8DC]"
                    }`}>
                    <s.icon className="h-3 w-3" /> {s.label.replace(/\s*\(\d+\)/, "")}
                  </button>
                ))}
              </div>

              {/* Mobile wallet summary */}
              <div className="mt-3 rounded-xl bg-gradient-to-r from-emerald-900 to-emerald-950 p-3 flex items-center justify-between">
                <div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">Available</div>
                  <div className="text-lg font-black text-emerald-300">{fmtUSD(wallet.available_capital || 0)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={fundingAmount} onChange={e => setFundingAmount(e.target.value)}
                    placeholder="$" className="w-20 px-2 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-xs placeholder-white/30 focus:outline-none" />
                  <button onClick={handleFundAccount} disabled={funding || !fundingAmount}
                    className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold disabled:opacity-40">
                    {funding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Conditional Access Banner */}
            {!isFullyEligible && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="conditional-access-banner">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <h4 className="text-sm font-bold text-amber-800">
                      {!isApproved ? (approvalState === "under_review" ? "Account Under Review" : "Conditional Access")
                        : accredStatus_computed === "pending_review" ? "Accreditation Under Review"
                        : accredStatus_computed === "rejected" ? "Accreditation Not Approved"
                        : "Complete Accreditation"}
                    </h4>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {computedState.blocked_reason || "Complete your investor profile and accreditation to unlock full access."}
                    </p>
                    {accredStatus_computed === "not_submitted" && (
                      <button onClick={() => setDashSection("how_it_works")}
                        className="mt-2 text-xs font-bold text-amber-700 hover:text-amber-800 underline"
                        data-testid="complete-profile-link">
                        Complete Your Profile
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── TAB CONTENT ── */}
            {dashSection === "fund_portal" && <FundPortalSection user={user} headers={headers} fetchData={fetchData} />}

            {dashSection === "portfolio" && (
              <div className="space-y-6">
                <InvestorPortfolioPanel />
                <PortfolioCharts commitments={myCommitments} distributions={recentDists} />
                <PortfolioView portfolio={portfolio} commitments={myCommitments} />
              </div>
            )}

            {dashSection === "servicing" && <InvestorServicingTab userId={user?.user_id} />}
            {dashSection === "distributions" && <DistributionsView distributions={recentDists} headers={headers} />}
            {dashSection === "auto_invest" && <AutoInvestPreferences />}
            {dashSection === "funds" && <InvestorFundsTab />}
            {dashSection === "deal_history" && <DealHistoryTab headers={headers} />}
            {dashSection === "how_it_works" && <InvestmentProcessTab />}
            {dashSection === "tax_exports" && <TaxExportPanel isAdmin={false} />}
            {dashSection === "messages" && <EntityComms entityType="investor" entityId={data?.user_id || ""} />}
            {dashSection === "profile" && (() => { navigateTo("profile"); return null; })()}

            {/* Commitments Tab */}
            {dashSection === "commitments" && (
              <div data-testid="investor-commitments">
                {myCommitments.length === 0 ? (
                  <div className="rounded-2xl bg-white border border-[#EDE8DC] p-12 text-center shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
                    <DollarSign className="h-12 w-12 mx-auto text-[#ddd] mb-3" />
                    <p className="text-[#888]">No capital commitments yet.</p>
                    <p className="text-xs text-[#aaa] mt-1">Browse open opportunities to deploy capital.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myCommitments.map(c => (
                      <CommitmentRow key={c.commitment_id} commitment={c} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── OPPORTUNITIES FEED ── */}
            {dashSection === "opportunities" && (
              <div className="space-y-4" data-testid="investor-opportunities">
                {/* YTD Distribution Summary */}
                {ytd.total_distributions > 0 && (
                  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="rounded-2xl border border-[#e8e0d0] bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-5 shadow-sm" data-testid="ytd-distribution-summary">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 rounded-xl bg-emerald-100 border border-emerald-200">
                          <TrendingUp className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-[#222] uppercase tracking-wider">{ytd.year} YTD Returns</h3>
                          <p className="text-[10px] text-[#999]">{ytd.distribution_count} distribution{ytd.distribution_count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-xl bg-white border border-[#e8e0d0] p-3 text-center">
                        <div className="text-lg font-black text-[#D4AF37]">{fmtUSD(ytd.interest_earned)}</div>
                        <div className="text-[9px] text-[#888] uppercase tracking-wider">Interest</div>
                      </div>
                      <div className="rounded-xl bg-white border border-[#e8e0d0] p-3 text-center">
                        <div className="text-lg font-black text-[#333]">{fmtUSD(ytd.principal_returned)}</div>
                        <div className="text-[9px] text-[#888] uppercase tracking-wider">Principal</div>
                      </div>
                      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
                        <div className="text-lg font-black text-emerald-600">{fmtUSD(ytd.total_distributions)}</div>
                        <div className="text-[9px] text-emerald-700 uppercase tracking-wider font-semibold">Total</div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Activity Feed — collapsible scrollable box */}
                {investorNotifs.length > 0 && (
                  <div className="rounded-2xl bg-white border border-[#EDE8DC] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.03)]">
                    <button
                      onClick={() => setFeedCollapsed(!feedCollapsed)}
                      className="w-full flex items-center justify-between px-5 py-3 text-xs font-semibold text-[#555] hover:bg-[#FDFCF9] transition-all"
                      data-testid="investor-feed-collapse-toggle"
                    >
                      <span className="flex items-center gap-2">
                        <Zap className="h-3.5 w-3.5 text-emerald-600" />
                        Activity Feed
                        <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">{investorNotifs.length}</span>
                      </span>
                      {feedCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                    </button>
                    {!feedCollapsed && (
                      <div className="max-h-[320px] overflow-y-auto border-t border-[#EDE8DC] divide-y divide-[#f0ebe0]" data-testid="investor-activity-feed-scroll">
                        {investorNotifs.map((n, i) => {
                          const nid = n.notif_id || n.notification_id || `iact-${i}`;
                          const entityId = n.entity_id || n.lead_id || n.deal_id || n.opportunity_id;
                          const entityType = n.entity_type || (n.opportunity_id ? "opportunity" : "deal");
                          return (
                            <div key={nid}
                              onClick={() => { if (entityId) navigateToEntity(entityType, entityId); }}
                              className="relative group/notif flex items-start gap-3 px-5 py-3 hover:bg-[#FDFCF9] transition-all cursor-pointer"
                              data-testid={`investor-feed-activity-${i}`}
                            >
                              <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                n.type === "distribution_posted" ? "bg-green-50 text-green-600" :
                                n.type === "commitment_confirmed" ? "bg-emerald-50 text-emerald-600" :
                                n.type === "deal_funded" ? "bg-[#E5C98B]/20 text-[#8B7340]" :
                                "bg-gray-50 text-gray-500"
                              }`}>
                                {n.type === "distribution_posted" ? <Banknote className="h-3.5 w-3.5" /> :
                                 n.type === "commitment_confirmed" ? <CheckCircle className="h-3.5 w-3.5" /> :
                                 n.type === "deal_funded" ? <TrendingUp className="h-3.5 w-3.5" /> :
                                 <Zap className="h-3.5 w-3.5" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                {accredStatus !== "verified" && (n.type === "opportunity_available" || n.type === "new_opportunity") ? (
                                  <>
                                    <p className="text-xs font-medium text-[#333] leading-relaxed">New Opportunity Available</p>
                                    <p className="text-[10px] text-[#888] mt-0.5 leading-relaxed">A new opportunity matching your profile is available. Complete your accreditation to access investment details.</p>
                                  </>
                                ) : (
                                  <>
                                    <p className="text-xs font-medium text-[#333] leading-relaxed">{n.title || n.message || "Platform update"}</p>
                                    {n.title && n.message && n.title !== n.message && (
                                      <p className="text-[10px] text-[#888] mt-0.5 leading-relaxed">{n.message}</p>
                                    )}
                                  </>
                                )}
                                <p className="text-[10px] text-[#aaa] mt-1">{_relativeTime(n.created_at)}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 mt-1">
                                {!n.read && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                                {entityId && <ChevronRight className="h-3.5 w-3.5 text-[#ccc] group-hover/notif:text-emerald-500 transition-colors" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Opportunity Cards */}
                {opportunities.length === 0 ? (
                  <div className="rounded-2xl bg-white border border-[#EDE8DC] p-12 text-center shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
                    <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
                      <Briefcase className="h-7 w-7 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0A0A0A] mb-2">No Fund Opportunities</h3>
                    <p className="text-sm text-[#555]">No fund opportunities available yet. Check back soon.</p>
                  </div>
                ) : (
                  opportunities.map((opp, i) => (
                    <motion.div key={opp.opportunity_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.04, 0.3) }}>
                      <OpportunityCard opp={opp}
                        onClick={() => setSelectedOpp(opp.opportunity_id)}
                        onInvest={canCommitCapital ? () => setCommitModal(opp) : null}
                        onExpressCommit={canCommitCapital ? () => setExpressModal(opp) : null}
                        entitlement={entitlement}
                        quickFund={quickFundMap[opp.opportunity_id]}
                        canCommitCapital={canCommitCapital} />
                    </motion.div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* ── RIGHT SIDEBAR: Portfolio Intelligence ── */}
          <div className="hidden xl:block">
            <div className="sticky top-6 space-y-4">
              {/* Portfolio Stats */}
              <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }}>
                <div style={{ padding: "12px 16px", borderBottom: "2px solid var(--ic-gold)" }}>
                  <h3 style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ic-green-dark)" }}>Portfolio Summary</h3>
                </div>
                <div style={{ padding: 12 }}>
                  {[
                    { label: "Projected Return", value: fmtUSD(portfolio.projected_return || 0), color: "var(--ic-green-dark)" },
                    { label: "Weighted Yield", value: `${portfolio.weighted_avg_yield || 0}%`, color: "var(--ic-gold)" },
                    { label: "Active Deals", value: portfolio.active_deals || 0, color: "var(--ic-text-primary)" },
                    { label: "Total Deployed", value: fmtUSD(portfolio.total_committed || 0), color: "var(--ic-text-primary)" },
                  ].map(s => (
                    <div key={s.label} style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: "var(--ic-text-secondary)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</span>
                      <span style={{ flex: 1, borderBottom: "1px dotted var(--ic-border-dark)", margin: "0 6px", marginBottom: 3, minWidth: 20 }} />
                      <span style={{ fontSize: 14, fontWeight: 700, color: s.color, whiteSpace: "nowrap" }}>{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Commitments */}
              {activeCommits.length > 0 && (
                <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }}>
                  <div style={{ padding: "12px 16px", borderBottom: "2px solid var(--ic-gold)" }}>
                    <h3 style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ic-green-dark)" }}>Active Commitments</h3>
                  </div>
                  <div style={{ padding: 12 }}>
                    {activeCommits.slice(0, 5).map(c => (
                      <div key={c.commitment_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid var(--ic-border)" }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ic-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {formatAddress((c.deal_snapshot || {}).property_address) || c.opportunity_title || "Investment"}
                          </div>
                          <div style={{ fontSize: 10, color: "var(--ic-text-secondary)" }}>{c.projected_yield ? `${c.projected_yield}%` : "—"} yield</div>
                        </div>
                        <span className="ic-status-badge" style={{
                          fontSize: 9, padding: "2px 8px",
                          color: c.status === "pending" ? "var(--ic-status-pending)" : c.status === "confirmed" ? "var(--ic-status-confirmed)" : c.status === "funded" ? "var(--ic-status-funded)" : "var(--ic-text-secondary)",
                          background: c.status === "pending" ? "rgba(217,119,6,0.1)" : c.status === "confirmed" ? "rgba(45,106,79,0.1)" : c.status === "funded" ? "rgba(27,67,50,0.1)" : "rgba(156,163,175,0.1)",
                        }}>
                          {(c.status || "").toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }}>
                <div style={{ padding: "12px 16px", borderBottom: "2px solid var(--ic-gold)" }}>
                  <h3 style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ic-green-dark)" }}>Quick Actions</h3>
                </div>
                <div style={{ padding: 8 }}>
                  {[
                    { icon: Briefcase, label: "Browse Opportunities", action: () => setDashSection("opportunities") },
                    { icon: PieChart, label: "View Portfolio", action: () => setDashSection("portfolio") },
                    { icon: Banknote, label: "Distributions", action: () => setDashSection("distributions") },
                    { icon: MessageCircle, label: "Messages", action: () => setDashSection("messages") },
                  ].map((a, i) => (
                    <button key={i} onClick={a.action}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: "var(--ic-radius)", fontSize: 12, color: "var(--ic-text-secondary)", fontWeight: 500, border: "none", background: "transparent", cursor: "pointer", transition: "all 0.15s ease", textAlign: "left" }}
                      onMouseOver={(e) => { e.currentTarget.style.background = "var(--ic-bg-row-alt)"; e.currentTarget.style.color = "var(--ic-green-dark)"; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--ic-text-secondary)"; }}
                    >
                      <a.icon style={{ width: 14, height: 14 }} /> {a.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Distributions */}
              {recentDists.length > 0 && (
                <div style={{ borderRadius: "var(--ic-radius-lg)", border: "1px solid var(--ic-border)", background: "var(--ic-bg-card)", overflow: "hidden", boxShadow: "var(--ic-shadow-sm)" }}>
                  <div style={{ padding: "12px 16px", borderBottom: "2px solid var(--ic-gold)" }}>
                    <h3 style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--ic-green-dark)" }}>Recent Returns</h3>
                  </div>
                  <div style={{ padding: 12 }}>
                    {recentDists.slice(0, 4).map((d, i) => (
                      <div key={d.distribution_id || i} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "6px 0", borderBottom: i < 3 ? "1px dotted var(--ic-border)" : "none" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ic-green-dark)" }}>+{fmtUSD(d.amount || 0)}</span>
                        <span style={{ fontSize: 10, color: "var(--ic-text-secondary)" }}>{d.created_at ? new Date(d.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fund Updates — in right sidebar */}
              <FundUpdatesPanel />
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity Detail Modal */}
      <AnimatePresence>
        {selectedOpp && (
          <OpportunityDetailModal oppId={selectedOpp} headers={headers}
            onClose={() => setSelectedOpp(null)}
            onInvest={(opp) => { setSelectedOpp(null); setCommitModal(opp); }}
            entitlement={entitlement} onEntityNavigate={navigateToEntity} />
        )}
      </AnimatePresence>

      {/* Commit Capital Modal */}
      <AnimatePresence>
        {commitModal && (
          <CommitCapitalModal opp={commitModal} headers={headers}
            onClose={() => setCommitModal(null)}
            onSuccess={() => { setCommitModal(null); fetchData(); }}
            entitlement={entitlement} />
        )}
      </AnimatePresence>

      {/* Express Commit Modal */}
      <AnimatePresence>
        {expressModal && (
          <ExpressCommitModal opp={expressModal}
            onClose={() => setExpressModal(null)}
            onSuccess={() => { setExpressModal(null); fetchData(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function _relativeTime(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

// ── Portfolio View ──────────────────────────────────────────
function PortfolioView({ portfolio, commitments }) {
  const activeCommits = commitments.filter(c => c.status !== "withdrawn");
  const typeBreakdown = portfolio.type_breakdown || {};
  const typeEntries = Object.entries(typeBreakdown).sort((a, b) => b[1] - a[1]);
  const totalForBreakdown = Object.values(typeBreakdown).reduce((s, v) => s + v, 0) || 1;

  return (
    <div className="space-y-6" data-testid="investor-portfolio">
      {/* Performance Overview */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Returns Summary */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Returns Summary</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-cream-warm">
              <span className="text-sm text-[#666]">Total Capital Deployed</span>
              <span className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(portfolio.total_committed || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-cream-warm">
              <span className="text-sm text-[#666]">Projected Total Return</span>
              <span className="text-lg font-bold text-emerald-600">{fmtUSD(portfolio.projected_return || 0)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-cream-warm">
              <span className="text-sm text-[#666]">Weighted Avg Yield</span>
              <span className="text-lg font-bold text-gold-dark">{portfolio.weighted_avg_yield || 0}%</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-[#666]">Total + Projected</span>
              <span className="text-xl font-bold text-[#0A0A0A]">{fmtUSD((portfolio.total_committed || 0) + (portfolio.projected_return || 0))}</span>
            </div>
          </div>
        </Card>

        {/* Allocation Breakdown */}
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Allocation by Loan Type</h4>
          {typeEntries.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#999]">No allocations yet</div>
          ) : (
            <div className="space-y-3">
              {typeEntries.map(([label, amount], i) => {
                const pct = ((amount / totalForBreakdown) * 100).toFixed(1);
                return (
                  <div key={label} data-testid={`allocation-${label.toLowerCase().replace(/ /g, '-')}`}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-[#333] font-medium">{label}</span>
                      <span className="text-[#666]">{fmtUSD(amount)} ({pct}%)</span>
                    </div>
                    <div className="h-2 rounded-full bg-cream-warm overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: i * 0.1 }}
                        className={`h-full rounded-full ${TYPE_COLORS[i % TYPE_COLORS.length]}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card hover={false}>
        <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Commitment Status</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Pending", count: portfolio.pending_count || 0, color: "bg-amber-100 text-amber-700 border-amber-200" },
            { label: "Confirmed", count: portfolio.confirmed_count || 0, color: "bg-blue-100 text-blue-700 border-blue-200" },
            { label: "Funded", count: portfolio.funded_count || 0, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
            { label: "Total Active", count: portfolio.active_deals || 0, color: "bg-purple-100 text-purple-700 border-purple-200" },
          ].map(s => (
            <div key={s.label} className={`rounded-xl border px-4 py-3 text-center ${s.color}`} data-testid={`status-${s.label.toLowerCase().replace(/ /g, '-')}`}>
              <div className="text-2xl font-bold">{s.count}</div>
              <div className="text-xs font-medium uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Investment History Table */}
      <Card hover={false}>
        <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Investment History</h4>
        {activeCommits.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#999]">No investments to display</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="investment-history-table">
              <thead>
                <tr className="border-b border-cream-warm text-left">
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Property</th>
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Type</th>
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Committed</th>
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Yield</th>
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Term</th>
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Proj. Return</th>
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Status</th>
                  <th className="pb-3 text-[10px] uppercase tracking-wider text-[#888] font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {activeCommits.map(c => {
                  const snap = c.deal_snapshot || {};
                  return (
                    <tr key={c.commitment_id} className="border-b border-cream-warm hover:bg-cream/50 transition-colors" data-testid={`history-row-${c.commitment_id}`}>
                      <td className="py-3 text-[#0A0A0A] font-medium max-w-[180px] truncate">{formatAddress(snap.property_address) || "—"}</td>
                      <td className="py-3"><span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700">{c.loan_type_label || LOAN_LABELS[snap.loan_type] || "—"}</span></td>
                      <td className="py-3 font-semibold text-[#0A0A0A]">{fmtUSD(c.amount || 0)}</td>
                      <td className="py-3 text-gold-dark font-medium">{c.projected_yield ? `${c.projected_yield}%` : "—"}</td>
                      <td className="py-3 text-[#555]">{c.projected_term_months ? `${c.projected_term_months}mo` : "—"}</td>
                      <td className="py-3 text-emerald-600 font-medium">{c.projected_return ? fmtUSD(c.projected_return) : "—"}</td>
                      <td className="py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_COLORS[c.status] || "bg-gray-100 text-gray-600"}`}>{(c.status || "").toUpperCase()}</span></td>
                      <td className="py-3 text-[#888] text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Opportunity Card ────────────────────────────────────────
function OpportunityCard({ opp, onClick, onInvest, onExpressCommit, entitlement, quickFund, canCommitCapital }) {
  const snap = opp.deal_snapshot || {};
  const isComingSoon = opp.status === "coming_soon";
  const pctRaised = opp.target_raise ? Math.min(100, (opp.raised_amount / opp.target_raise) * 100) : 0;
  const alreadyCommitted = !!opp.my_commitment;
  const canCommit = canCommitCapital && onInvest && !isComingSoon;

  const BADGE_CONFIG = {
    almost_funded: { label: "Almost Funded", color: "bg-red-100 text-red-600 border-red-200" },
    hot: { label: "Hot Deal", color: "bg-amber-100 text-amber-600 border-amber-200" },
    closing_soon: { label: "Closing Soon", color: "bg-orange-100 text-orange-600 border-orange-200" },
    popular: { label: "Popular", color: "bg-blue-100 text-blue-600 border-blue-200" },
  };
  const badge = quickFund?.badge ? BADGE_CONFIG[quickFund.badge] : null;

  if (isComingSoon) {
    return (
      <Card hover={false}>
        <div className="relative" data-testid={`opp-card-coming-soon-${opp.opportunity_id}`}>
          {/* Coming Soon Banner */}
          <div className="flex items-center justify-between mb-3">
            <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200" data-testid={`coming-soon-badge-${opp.opportunity_id}`}>
              Coming Soon
            </span>
          </div>
          {/* Basic info only */}
          <div className="space-y-2 mb-4">
            {(opp.opportunity_type || snap.loan_type) && (
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700">
                  {opp.opportunity_type || opp.loan_type_label || LOAN_LABELS[snap.loan_type] || snap.loan_type}
                </span>
              </div>
            )}
            {snap.property_address && (
              <div className="flex items-center gap-1.5 text-xs text-[#555]">
                <Building2 className="h-3 w-3 text-[#999]" /> {formatAddress(snap.property_address)}
              </div>
            )}
            {snap.state && !snap.property_address && (
              <div className="flex items-center gap-1.5 text-xs text-[#666]">
                <MapPin className="h-3 w-3 text-[#999]" /> {snap.state}
              </div>
            )}
            {snap.property_type && (
              <div className="text-xs text-[#888]">
                {snap.property_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
              </div>
            )}
          </div>
          {/* Teaser footer */}
          <div className="pt-3 border-t border-cream-warm">
            <div className="w-full py-2.5 rounded-xl bg-indigo-50 border border-indigo-100 text-center" data-testid={`opp-coming-soon-${opp.opportunity_id}`}>
              <div className="flex items-center justify-center gap-2 text-sm text-indigo-600 font-medium">
                <Clock className="h-4 w-4" />
                Full details available soon
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card hover={true}>
      <div className="cursor-pointer" onClick={onClick} data-testid={`opp-card-${opp.opportunity_id}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700">
              {opp.opportunity_type || opp.loan_type_label || LOAN_LABELS[snap.loan_type] || snap.loan_type}
            </span>
            {snap.state && <span className="text-[10px] text-[#888]">{snap.state}</span>}
            {opp.current_stage && (
              <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-teal-50 text-teal-600 border border-teal-200" data-testid={`opp-stage-${opp.opportunity_id}`}>
                {STAGE_LABELS[opp.current_stage] || opp.current_stage.replace(/_/g, " ")}
              </span>
            )}
            {badge && (
              <span className={`px-1.5 py-0.5 rounded-md text-[9px] font-bold border ${badge.color}`} data-testid={`quick-fund-badge-${opp.opportunity_id}`}>
                {badge.label}
              </span>
            )}
          </div>
          {opp.projected_yield && (
            <span className="text-sm font-bold text-gold-dark">{opp.projected_yield}% yield</span>
          )}
        </div>

        <div className="text-sm font-semibold text-[#0A0A0A] mb-1">
          {formatAddress(opp.title || snap.property_address) || "Investment Opportunity"}
        </div>
        <OpportunityScoreCard opportunityId={opp.opportunity_id} compact />

        <div className="grid grid-cols-3 gap-2 text-center my-3 py-2.5 bg-cream rounded-xl border border-cream-warm">
          {opp.fund_id ? (
            <>
              <div>
                <div className="text-xs font-bold text-[#0A0A0A]">{opp.projected_yield ? `${opp.projected_yield}%` : (opp.extra?.projected_irr ? `${opp.extra.projected_irr}%` : "N/A")}</div>
                <div className="text-[10px] text-[#888]">Target Yield</div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#0A0A0A]">{opp.target_raise ? fmtUSD(opp.target_raise) : "N/A"}</div>
                <div className="text-[10px] text-[#888]">Fund Size</div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#0A0A0A]">{opp.projected_term_months || "\u2014"}mo</div>
                <div className="text-[10px] text-[#888]">Term</div>
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="text-xs font-bold text-[#0A0A0A]">{snap.purchase_price ? fmtUSD(snap.purchase_price) : "N/A"}</div>
                <div className="text-[10px] text-[#888]">Price</div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#0A0A0A]">{snap.arv ? fmtUSD(snap.arv) : "N/A"}</div>
                <div className="text-[10px] text-[#888]">ARV</div>
              </div>
              <div>
                <div className="text-xs font-bold text-[#0A0A0A]">{opp.projected_term_months || "\u2014"}mo</div>
                <div className="text-[10px] text-[#888]">Term</div>
              </div>
            </>
          )}
        </div>

        {/* Raise progress */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-[#666]">{fmtUSD(opp.raised_amount)} raised</span>
            <span className="text-[#888]">of {fmtUSD(opp.target_raise)}</span>
          </div>
          <div className="h-2 rounded-full bg-cream-warm overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${pctRaised}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full rounded-full bg-emerald-500" />
          </div>
          <div className="flex items-center justify-between text-[10px] text-[#aaa] mt-1.5">
            <div className="flex items-center gap-2">
              {opp.investor_count > 0 && <SocialProofBadge text={`${opp.investor_count} investor${opp.investor_count !== 1 ? "s" : ""}`} variant="interest" />}
              {pctRaised >= 70 && <SocialProofBadge text={`${Math.round(pctRaised)}% funded`} variant="urgency" />}
            </div>
            <span>Min {fmtUSD(opp.min_investment)}</span>
          </div>
        </div>
      </div>

      <div className="pt-3 border-t border-cream-warm">
        {alreadyCommitted ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600 font-medium" data-testid={`opp-committed-${opp.opportunity_id}`}>
            <CheckCircle className="h-4 w-4" /> Committed {fmtUSD(opp.my_commitment.amount)}
          </div>
        ) : canCommit ? (
          <div className="flex gap-2">
            <button onClick={(e) => { e.stopPropagation(); onInvest(); }}
              data-testid={`opp-invest-${opp.opportunity_id}`}
              className="flex-1 py-2.5 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 transition-colors flex items-center justify-center gap-2">
              <DollarSign className="h-4 w-4" /> Invest
            </button>
            <button onClick={(e) => { e.stopPropagation(); onExpressCommit(); }}
              data-testid={`opp-express-${opp.opportunity_id}`}
              className="px-3 py-2.5 rounded-xl border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors flex items-center gap-1.5"
              title="Express Commit">
              <Zap className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="w-full py-2.5 rounded-xl bg-gray-100 border border-gray-200 text-center" data-testid={`opp-locked-${opp.opportunity_id}`}>
            <div className="flex items-center justify-center gap-2 text-sm text-[#888] font-medium">
              <Shield className="h-4 w-4" />
              {entitlement === "unverified" ? "Verification required to invest" :
               entitlement === "verified_unaccredited" ? "Accreditation required to invest" :
               "Complete onboarding to invest"}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Commitment Row ──────────────────────────────────────────
function CommitmentRow({ commitment }) {
  const c = commitment;
  const snap = c.deal_snapshot || {};

  return (
    <Card hover={false}>
      <div className="flex items-center justify-between" data-testid={`commitment-${c.commitment_id}`}>
        <div className="flex items-center gap-4">
          <div className="p-2.5 rounded-xl bg-cream border border-cream-warm">
            <DollarSign className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-[#0A0A0A]">{formatAddress(snap.property_address) || c.opportunity_title || "Investment"}</div>
            <div className="flex items-center gap-2 text-xs text-[#888]">
              <span>{c.loan_type_label || LOAN_LABELS[snap.loan_type] || "—"}</span>
              <span>&bull;</span>
              <span>{snap.state || "—"}</span>
              {c.projected_yield && <><span>&bull;</span><span className="text-gold-dark">{c.projected_yield}% yield</span></>}
              {c.projected_term_months && <><span>&bull;</span><span>{c.projected_term_months}mo</span></>}
              {c.current_stage && <><span>&bull;</span><span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold bg-teal-50 text-teal-600" data-testid={`commitment-stage-${c.commitment_id}`}>{STAGE_LABELS[c.current_stage] || c.current_stage.replace(/_/g, " ")}</span></>}
            </div>
          </div>
        </div>
        <div className="text-right space-y-1">
          <div className="text-lg font-bold text-[#0A0A0A]">{fmtUSD(c.amount)}</div>
          {c.projected_return && (
            <div className="text-xs text-emerald-600 font-medium">+{fmtUSD(c.projected_return)} proj.</div>
          )}
          {c.deal_returns?.moic != null && (
            <div className="text-xs text-teal-600 font-medium" data-testid={`commitment-moic-${c.commitment_id}`}>{c.deal_returns.moic.toFixed(2)}x MOIC</div>
          )}
          {c.deal_returns?.irr_annualized != null && c.deal_returns?.irr_status === "calculated" && (
            <div className="text-xs text-teal-500" data-testid={`commitment-irr-${c.commitment_id}`}>{c.deal_returns.irr_annualized.toFixed(1)}% IRR</div>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_COLORS[c.status] || "bg-gray-100 text-gray-600"}`}>
            {(c.status || "").toUpperCase()}
          </span>
        </div>
      </div>
    </Card>
  );
}

// ── Opportunity Detail Modal ────────────────────────────────
function OpportunityDetailModal({ oppId, headers, onClose, onInvest, entitlement, onEntityNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/investor/opportunities/${oppId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [oppId]);

  if (loading || !data) return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </motion.div>
  );

  const opp = data.opportunity;
  const deal = data.deal || {};
  const snap = opp.deal_snapshot || {};

  // Calculate projected return for this opportunity
  const projReturn = opp.projected_yield && opp.projected_term_months
    ? (opp.target_raise * (opp.projected_yield / 100) * (opp.projected_term_months / 12)).toFixed(0)
    : null;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4 sm:mx-0"
        onClick={e => e.stopPropagation()} data-testid="opp-detail-modal">
        <div className="sticky top-0 bg-white border-b border-cream-warm px-4 sm:px-6 py-4 z-10 flex items-start justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-[#0A0A0A]">{formatAddress(opp.title || snap.property_address) || "Investment Opportunity"}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700">
                {opp.opportunity_type || opp.loan_type_label || LOAN_LABELS[snap.loan_type]}
              </span>
              {snap.state && <span className="text-xs text-[#888]">{snap.state}</span>}
              {opp.projected_yield && <span className="text-xs font-bold text-gold-dark">{opp.projected_yield}% projected yield</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onEntityNavigate && <button onClick={() => { onClose(); onEntityNavigate("opportunity", oppId); }} data-testid="opp-view-360-btn"
              className="px-3 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-colors">
              View 360
            </button>}
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-cream"><X className="h-5 w-5 text-[#888]" /></button>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-6">
          {/* Financial grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(opp.fund_id ? [
              { label: "Target Yield", value: opp.projected_yield ? `${opp.projected_yield}%` : (opp.extra?.projected_irr ? `${opp.extra.projected_irr}%` : "N/A") },
              { label: "Fund Size", value: fmtUSD(opp.target_raise) },
              { label: "Min Investment", value: fmtUSD(opp.min_investment) },
              { label: "Term", value: opp.projected_term_months ? `${opp.projected_term_months} months` : "N/A" },
              { label: "Strategy", value: opp.extra?.strategy || "N/A" },
              { label: "Collateral", value: opp.extra?.collateral || "N/A" },
            ] : [
              { label: "Purchase Price", value: snap.purchase_price ? fmtUSD(snap.purchase_price) : "N/A" },
              { label: "ARV", value: snap.arv ? fmtUSD(snap.arv) : "N/A" },
              { label: "Rehab Budget", value: snap.rehab_budget ? fmtUSD(snap.rehab_budget) : "N/A" },
              { label: "Target Raise", value: fmtUSD(opp.target_raise) },
              { label: "Min Investment", value: fmtUSD(opp.min_investment) },
              { label: "Term", value: opp.projected_term_months ? `${opp.projected_term_months} months` : "N/A" },
            ]).map(f => (
              <div key={f.label} className="p-3 rounded-xl bg-cream border border-cream-warm text-center">
                <div className="text-sm font-bold text-[#0A0A0A]">{f.value}</div>
                <div className="text-[10px] text-[#888]">{f.label}</div>
              </div>
            ))}
          </div>

          {/* LTV / LTC Metrics */}
          {(deal.ltv || deal.ltc) && (
            <div className="grid grid-cols-2 gap-3">
              {deal.ltv && (
                <div className={`p-3 rounded-xl border text-center ${deal.ltv <= 70 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                  <div className={`text-lg font-bold ${deal.ltv <= 70 ? "text-emerald-600" : "text-amber-600"}`}>{deal.ltv}%</div>
                  <div className="text-[10px] text-[#888]">Loan to Value (LTV)</div>
                </div>
              )}
              {deal.ltc && (
                <div className={`p-3 rounded-xl border text-center ${deal.ltc <= 80 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                  <div className={`text-lg font-bold ${deal.ltc <= 80 ? "text-emerald-600" : "text-amber-600"}`}>{deal.ltc}%</div>
                  <div className="text-[10px] text-[#888]">Loan to Cost (LTC)</div>
                </div>
              )}
            </div>
          )}

          {/* Projected returns box */}
          {(opp.projected_yield || projReturn) && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <h4 className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">Projected Returns</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-700">{opp.projected_yield}%</div>
                  <div className="text-[10px] text-emerald-600">Annual Yield</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-700">{opp.projected_term_months || "—"}mo</div>
                  <div className="text-[10px] text-emerald-600">Term</div>
                </div>
                {projReturn && (
                  <div className="text-center">
                    <div className="text-lg font-bold text-emerald-700">{fmtUSD(parseInt(projReturn))}</div>
                    <div className="text-[10px] text-emerald-600">Total Return (est.)</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Raise progress */}
          <div className="p-4 rounded-xl bg-cream border border-cream-warm">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-semibold text-[#0A0A0A]">{fmtUSD(opp.raised_amount)} raised</span>
              <span className="text-[#888]">of {fmtUSD(opp.target_raise)}</span>
            </div>
            <div className="h-3 rounded-full bg-white overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${Math.min(100, (opp.raised_amount / opp.target_raise) * 100)}%` }} />
            </div>
            <div className="text-xs text-[#888] mt-2">{opp.investor_count} investor{opp.investor_count !== 1 ? "s" : ""} committed</div>
          </div>

          {/* Borrower Track Record */}
          {data.borrower_track_record && (
            <div className="rounded-xl border border-[#e8e0d0] bg-[#faf7f2] p-4" data-testid="modal-borrower-track">
              <h4 className="text-xs font-bold text-[#444] mb-3 flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-blue-500" /> Borrower Track Record
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="text-center">
                  <div className="text-lg font-bold text-[#0A0A0A]">
                    {data.borrower_track_record.experience_projects_completed || data.borrower_track_record.total_projects || 0}
                  </div>
                  <div className="text-[9px] text-[#999] uppercase">Projects</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-emerald-600">{data.borrower_track_record.funded_projects || 0}</div>
                  <div className="text-[9px] text-[#999] uppercase">Platform Funded</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-[#b89d5a]">{fmtUSD(data.borrower_track_record.total_volume || 0)}</div>
                  <div className="text-[9px] text-[#999] uppercase">Volume</div>
                </div>
              </div>
            </div>
          )}

          {/* Risk Disclosure */}
          {data.risk_disclosure && (data.risk_disclosure.deal_highlights || data.risk_disclosure.key_considerations) && (
            <div className="space-y-3" data-testid="modal-risk-disclosure">
              {data.risk_disclosure.deal_highlights && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                  <h4 className="text-xs font-bold text-emerald-700 mb-2 flex items-center gap-1.5">
                    <TrendingUp className="h-3.5 w-3.5" /> Deal Highlights
                  </h4>
                  {typeof data.risk_disclosure.deal_highlights === "string" ? (
                    <p className="text-sm text-[#555]">{data.risk_disclosure.deal_highlights}</p>
                  ) : Array.isArray(data.risk_disclosure.deal_highlights) ? (
                    <ul className="space-y-1">
                      {data.risk_disclosure.deal_highlights.map((h, i) => (
                        <li key={i} className="text-sm text-[#555] flex items-start gap-1.5">
                          <CheckCircle className="h-3.5 w-3.5 text-emerald-400 shrink-0 mt-0.5" /> {h}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )}
              {data.risk_disclosure.key_considerations && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                  <h4 className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Key Considerations
                  </h4>
                  {typeof data.risk_disclosure.key_considerations === "string" ? (
                    <p className="text-sm text-[#666]">{data.risk_disclosure.key_considerations}</p>
                  ) : Array.isArray(data.risk_disclosure.key_considerations) ? (
                    <ul className="space-y-1">
                      {data.risk_disclosure.key_considerations.map((c, i) => (
                        <li key={i} className="text-sm text-[#666] flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" /> {c}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {opp.notes && (
            <div>
              <h4 className="text-xs font-bold text-[#444] mb-1">Notes</h4>
              <p className="text-sm text-[#666]">{opp.notes}</p>
            </div>
          )}

          {/* Documents */}
          {data.documents?.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-[#444] mb-2">Deal Documents</h4>
              <div className="space-y-2">
                {data.documents.map(d => (
                  <div key={d.doc_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-cream border border-cream-warm">
                    <FileText className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-[#333] flex-1 truncate">{d.original_filename}</span>
                    <span className="text-[10px] text-[#888]">{(d.doc_type || "").replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deal Q&A */}
          <DealQA opportunityId={oppId} />

          {/* PDF Downloads */}
          <div>
            <h4 className="text-xs font-bold text-[#444] mb-2">Reports</h4>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => downloadReport(opp.lead_id, "deal-deck")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-sm text-emerald-700 font-medium hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                data-testid="opp-download-deal-deck">
                <Download className="h-4 w-4" /> Deal Deck
              </button>
              <button onClick={() => downloadReport(opp.lead_id, "summary")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-cream-warm bg-cream text-sm text-[#444] hover:text-[#0A0A0A] hover:border-emerald-300 transition-colors"
                data-testid="opp-download-summary">
                <Download className="h-4 w-4" /> Deal Summary PDF
              </button>
              <button onClick={() => downloadReport(opp.lead_id, "underwriting")}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-cream-warm bg-cream text-sm text-[#444] hover:text-[#0A0A0A] hover:border-emerald-300 transition-colors"
                data-testid="opp-download-underwriting">
                <Download className="h-4 w-4" /> Underwriting Report
              </button>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-cream-warm px-4 sm:px-6 py-4 rounded-b-2xl">
          {data.my_commitment ? (
            <div className="flex items-center gap-2 text-emerald-600 font-medium text-sm">
              <CheckCircle className="h-5 w-5" /> You've committed {fmtUSD(data.my_commitment.amount)} — Status: {data.my_commitment.status}
            </div>
          ) : (entitlement === "accredited" || entitlement === "committed") ? (
            <button onClick={() => onInvest(opp)} data-testid="opp-detail-invest-btn"
              className="w-full py-3 rounded-xl bg-emerald-700 text-white font-semibold hover:bg-emerald-800 transition-colors flex items-center justify-center gap-2">
              <DollarSign className="h-5 w-5" /> Commit Capital
            </button>
          ) : (
            <div className="w-full py-3 rounded-xl bg-gray-100 border border-gray-200 text-center" data-testid="opp-detail-locked">
              <div className="flex items-center justify-center gap-2 text-sm text-[#888] font-medium">
                <Shield className="h-4 w-4" />
                {entitlement === "unverified" ? "Complete verification to commit capital" :
                 entitlement === "verified_unaccredited" ? "Accreditation required to commit capital" :
                 "Complete onboarding to commit capital"}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Commit Capital Modal ────────────────────────────────────
function CommitCapitalModal({ opp, headers, onClose, onSuccess, entitlement }) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Calculate projected return preview
  const numAmount = parseFloat(amount) || 0;
  const projReturn = numAmount && opp.projected_yield && opp.projected_term_months
    ? (numAmount * (opp.projected_yield / 100) * (opp.projected_term_months / 12)).toFixed(0)
    : null;

  const handleSubmit = async () => {
    if (!numAmount || numAmount < opp.min_investment) {
      setError(`Minimum investment is ${fmtUSD(opp.min_investment)}`);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/investor/commit`, {
        opportunity_id: opp.opportunity_id,
        amount: numAmount,
        notes: notes || null,
      }, { headers });
      toast.success(`${fmtUSD(numAmount)} committed successfully`);
      setSuccess(true);
      setTimeout(() => onSuccess(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to commit");
    } finally { setSubmitting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 sm:mx-0"
        onClick={e => e.stopPropagation()} data-testid="commit-capital-modal">
        <div className="px-4 sm:px-6 py-4 border-b border-cream-warm">
          <h3 className="text-base font-bold text-[#0A0A0A]">Commit Capital</h3>
          <p className="text-xs text-[#888]">{formatAddress((opp.deal_snapshot || {}).property_address) || "Investment"}</p>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-emerald-600 mb-3" />
            <p className="font-semibold text-[#0A0A0A]">Commitment Submitted!</p>
            <p className="text-xs text-[#888] mt-1">You'll be notified when it's confirmed.</p>
          </div>
        ) : (
          <>
            <div className="px-4 sm:px-6 py-5 space-y-4">
              <div className="p-3 rounded-xl bg-cream border border-cream-warm">
                <div className="flex justify-between text-xs">
                  <span className="text-[#888]">Target raise</span>
                  <span className="font-semibold">{fmtUSD(opp.target_raise)}</span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-[#888]">Min investment</span>
                  <span className="font-semibold">{fmtUSD(opp.min_investment)}</span>
                </div>
                {opp.projected_yield && (
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-[#888]">Projected yield</span>
                    <span className="font-semibold text-gold-dark">{opp.projected_yield}%</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-[#444] block mb-1">Investment Amount ($) *</label>
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none"
                  placeholder={`Min ${fmtUSD(opp.min_investment)}`}
                  data-testid="commit-amount-input" />
              </div>

              {/* Live projected return preview */}
              {projReturn && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-emerald-600">Projected Return</span>
                    <span className="text-sm font-bold text-emerald-700" data-testid="projected-return-preview">+{fmtUSD(parseInt(projReturn))}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-emerald-600">Total Back (est.)</span>
                    <span className="text-sm font-bold text-emerald-700">{fmtUSD(numAmount + parseInt(projReturn))}</span>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-[#444] block mb-1">Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-cream-warm text-sm focus:border-emerald-400 outline-none resize-none h-16"
                  placeholder="Any conditions or notes..." data-testid="commit-notes-input" />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-xs">
                  <AlertCircle className="h-3.5 w-3.5" /> {error}
                </div>
              )}
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-cream-warm flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 rounded-xl border border-cream-warm text-sm text-[#666]">Cancel</button>
              <button onClick={handleSubmit} disabled={submitting}
                data-testid="commit-submit-btn"
                className="px-5 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50 transition-colors flex items-center gap-2">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Confirm Commitment
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}


// ── Distributions View ──────────────────────────────────────
function DistributionsView({ distributions, headers }) {
  const [allDists, setAllDists] = useState(distributions || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/capital/distributions`, { headers })
      .then(r => setAllDists(r.data.distributions || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalPrincipal = allDists.reduce((s, d) => s + (d.principal_returned || 0), 0);
  const totalInterest = allDists.reduce((s, d) => s + (d.interest_earned || 0), 0);
  const totalReceived = totalPrincipal + totalInterest;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

  return (
    <div className="space-y-6" data-testid="distributions-view">
      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card hover={false}>
          <div className="text-center py-2">
            <div className="text-[10px] text-[#999] uppercase tracking-wider mb-1">Total Received</div>
            <div className="text-3xl font-black text-emerald-600">{fmtUSD(totalReceived)}</div>
          </div>
        </Card>
        <Card hover={false}>
          <div className="text-center py-2">
            <div className="text-[10px] text-[#999] uppercase tracking-wider mb-1">Principal Returned</div>
            <div className="text-2xl font-bold text-[#333]">{fmtUSD(totalPrincipal)}</div>
          </div>
        </Card>
        <Card hover={false}>
          <div className="text-center py-2">
            <div className="text-[10px] text-[#999] uppercase tracking-wider mb-1">Interest Earned</div>
            <div className="text-2xl font-bold text-gold-dark">{fmtUSD(totalInterest)}</div>
          </div>
        </Card>
      </div>

      {/* Distributions Table */}
      {allDists.length === 0 ? (
        <Card hover={false}>
          <div className="text-center py-12">
            <Banknote className="h-12 w-12 text-emerald-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#0A0A0A] mb-2">No Distributions Yet</h3>
            <p className="text-sm text-[#666]">Returns will appear here as your investments mature and loans pay off.</p>
          </div>
        </Card>
      ) : (
        <Card hover={false}>
          <h4 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wider mb-4">Distribution History</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cream-warm text-[10px] uppercase tracking-wider text-[#999]">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Deal</th>
                  <th className="text-right py-2">Principal</th>
                  <th className="text-right py-2">Interest</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {allDists.map((d, i) => (
                  <tr key={d.distribution_id || i} className="border-b border-cream-warm/50 hover:bg-cream/50 transition-colors">
                    <td className="py-2.5 text-[#666]">{new Date(d.created_at).toLocaleDateString()}</td>
                    <td className="py-2.5">
                      <span className="font-medium text-[#333]">{d.loan_type_label || "Real Estate"}</span>
                    </td>
                    <td className="py-2.5 text-right text-[#666]">{fmtUSD(d.principal_returned || 0)}</td>
                    <td className="py-2.5 text-right text-gold-dark font-medium">{fmtUSD(d.interest_earned || 0)}</td>
                    <td className="py-2.5 text-right font-bold text-emerald-600">{fmtUSD(d.total_distribution || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// Removed: InvestorSecondaryTab and FlywheelDashboardTab — beta dashboard normalization
// These features remain accessible via dedicated routes (/secondary-market) but are removed from the investor dashboard sidebar


function InvestorServicingTab({ userId }) {
  const [servicing, setServicing] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/servicing/investor/${userId}`)
      .then(r => setServicing(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);
  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  return <InvestorServicingPanel servicing={servicing} />;
}


// ── Fund Portal Section ──────────────────────────────────
function FundPortalSection({ user, headers, fetchData }) {
  const [fundStatus, setFundStatus] = useState(null);
  const [fundOpps, setFundOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memoOpp, setMemoOpp] = useState(null);
  const [subscribeOpp, setSubscribeOpp] = useState(null);

  const loadData = useCallback(() => {
    Promise.all([
      axios.get(`${API}/fund-portal/fund-status`, { headers }),
      axios.get(`${API}/fund-portal/opportunities`, { headers }),
    ]).then(([s, o]) => {
      setFundStatus(s.data);
      setFundOpps(o.data.opportunities || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleWaitlistJoin = async () => {
    try {
      await axios.post(`${API}/fund-portal/waitlist/join`, {}, { headers });
      toast.success("You've been added to the waitlist!");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join waitlist");
    }
  };

  const handleSubscribeSuccess = () => {
    setSubscribeOpp(null);
    loadData();
    fetchData();
    toast.success("Subscription submitted!");
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="fund-portal-section">
      {/* Waitlist Banner */}
      <WaitlistBanner status={fundStatus} onJoin={handleWaitlistJoin} />

      {/* Fund Opportunities */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ic-green-dark)", paddingBottom: 8, borderBottom: "2px solid var(--ic-gold)", flex: 1, marginRight: 16 }}>
            <Building2 style={{ width: 16, height: 16 }} />
            Fund Opportunities
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "rgba(64,145,108,0.1)", color: "var(--ic-status-open)" }}>
            {fundOpps.length} Fund{fundOpps.length !== 1 ? "s" : ""}
          </span>
        </div>

        {fundOpps.length === 0 ? (
          <div className="rounded-2xl bg-white border border-[#EDE8DC] p-10 text-center shadow-[0_2px_20px_rgba(0,0,0,0.03)]">
            <Building2 className="h-10 w-10 mx-auto text-[#ddd] mb-3" />
            <p className="text-sm text-[#888] font-medium">No fund opportunities available yet.</p>
            <p className="text-xs text-[#bbb] mt-1">Check back soon or join the waitlist to be notified.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {fundOpps.map(opp => (
              <FundOpportunityCard
                key={opp.opportunity_id}
                opp={opp}
                onViewMemo={setMemoOpp}
                onSubscribe={setSubscribeOpp}
              />
            ))}
          </div>
        )}
      </div>

      {/* Portfolio Summary — full width */}
      <FundPortfolioPanel />

      {/* Modals */}
      <AnimatePresence>
        {memoOpp && <FundMemoModal opp={memoOpp} onClose={() => setMemoOpp(null)} />}
        {subscribeOpp && (
          <SubscriptionModal opp={subscribeOpp} onClose={() => setSubscribeOpp(null)} onSuccess={handleSubscribeSuccess} />
        )}
      </AnimatePresence>
    </div>
  );
}
